import React, { useEffect, useState } from 'react';
import { login } from '../lib/auth';
import { api } from '../lib/api';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArgusMark } from '../components/cockpit';
import { AccessRequestModal } from '../components/login/AccessRequestModal';

// Deliberate product decision: the login screen always renders in English,
// regardless of the app's selected language (including for returning Hebrew
// users) — the brand copy here is authored English marketing copy, and a
// translated version read as flat/awkward next to it. Every screen AFTER
// login fully respects the user's language choice. Do not wire this page to
// i18next without checking with product first.

// Scoped page styles — every selector is namespaced under `.argus-login` and
// every class is prefixed `al-` so nothing here collides with the global app
// styles or Tailwind utilities.
const LOGIN_CSS = `
.argus-login{
  --a:#4F8DF5; --b:#A06BFF; --void:#05060B; --panel:#0B0E17;
  --hair:rgba(255,255,255,0.08); --hair2:rgba(255,255,255,0.14);
  --altext:#E9EDF6; --altext2:#9AA3B6; --altext3:#5A6379;
  --almono:'JetBrains Mono',ui-monospace,monospace;
  --alui:'Manrope',system-ui,sans-serif;
  --aldisp:'Manrope',system-ui,sans-serif;
  min-height:100vh; background:var(--void); color:var(--altext);
  font-family:var(--alui); -webkit-font-smoothing:antialiased;
}
.argus-login *{ box-sizing:border-box; }
.argus-login .al-stage{ display:grid; grid-template-columns:1.05fr 0.95fr; min-height:100vh; }

.argus-login .al-brandside{
  position:relative; overflow:hidden;
  background:
    radial-gradient(120% 90% at 18% 12%, rgba(79,141,245,0.16), transparent 55%),
    radial-gradient(120% 100% at 88% 92%, rgba(160,107,255,0.16), transparent 55%),
    linear-gradient(180deg,#080A12,#05060B);
  border-right:1px solid var(--hair);
  padding:48px 56px; display:flex; flex-direction:column;
}
.argus-login .al-grid{
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px);
  background-size:46px 46px;
  -webkit-mask-image:radial-gradient(120% 90% at 50% 45%, #000 40%, transparent 88%);
  mask-image:radial-gradient(120% 90% at 50% 45%, #000 40%, transparent 88%);
}
.argus-login .al-blob{
  position:absolute; width:520px; height:520px; border-radius:50%;
  filter:blur(70px); opacity:0.5; pointer-events:none;
  background:radial-gradient(circle, rgba(120,124,255,0.35), transparent 62%);
  top:-120px; right:-120px; animation:al-drift 18s ease-in-out infinite alternate;
}
@keyframes al-drift{ to{ transform:translate(-70px,120px) scale(1.15); } }

.argus-login .al-topbar{ position:relative; display:flex; align-items:center; gap:13px; }
.argus-login .al-wm{ font-family:var(--aldisp); font-weight:600; font-size:21px; letter-spacing:-0.02em; }
.argus-login .al-wm b{ font-weight:600; background:linear-gradient(120deg,var(--a),var(--b)); -webkit-background-clip:text; background-clip:text; color:transparent; }
.argus-login .al-wm i{ font-style:normal; color:var(--altext2); font-weight:500; margin-left:2px; }

.argus-login .al-center{ position:relative; align-self:center; margin:auto 0; max-width:440px; }
.argus-login .al-eye{ margin-bottom:32px; filter:drop-shadow(0 12px 40px rgba(120,124,255,0.35)); }
.argus-login .al-headline{ font-family:var(--aldisp); font-weight:600; font-size:40px; line-height:1.08; letter-spacing:-0.028em; margin:0; color:var(--altext); }
.argus-login .al-headline b{ background:linear-gradient(120deg,var(--a),var(--b)); -webkit-background-clip:text; background-clip:text; color:transparent; font-weight:600; }
.argus-login .al-sub{ color:var(--altext2); font-size:15.5px; line-height:1.6; margin:20px 0 0; max-width:400px; }

.argus-login .al-formside{ display:flex; align-items:center; justify-content:center; padding:48px; background:var(--void); }
.argus-login .al-card{ width:100%; max-width:392px; }

.argus-login .al-mobilebrand{ display:none; align-items:center; gap:11px; margin-bottom:28px; }
.argus-login .al-kicker{ font-family:var(--almono); font-size:11px; letter-spacing:.22em; text-transform:uppercase; color:var(--altext3); }
.argus-login .al-h1{ font-family:var(--aldisp); font-weight:600; font-size:30px; letter-spacing:-0.02em; margin:12px 0 8px; color:var(--altext); }
.argus-login .al-lede{ color:var(--altext2); font-size:14px; margin:0 0 26px; }

.argus-login .al-btn-ghost{
  display:flex; align-items:center; justify-content:center; gap:11px;
  width:100%; height:48px; border-radius:12px; cursor:pointer;
  background:linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015));
  border:1px solid var(--hair2); color:var(--altext);
  font-family:var(--alui); font-weight:600; font-size:14px;
  transition:border-color .18s, background .18s, transform .06s;
}
.argus-login .al-btn-ghost:hover{ border-color:rgba(255,255,255,0.3); background:rgba(255,255,255,0.06); }
.argus-login .al-btn-ghost:active{ transform:translateY(1px); }
.argus-login .al-btn-ghost:disabled{ opacity:.6; cursor:not-allowed; }
.argus-login .al-btn-ghost svg{ width:18px; height:18px; }

.argus-login .al-divider{ display:flex; align-items:center; gap:14px; margin:22px 0; }
.argus-login .al-divider::before,.argus-login .al-divider::after{ content:''; height:1px; flex:1; background:var(--hair); }
.argus-login .al-divider span{ font-family:var(--almono); font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:var(--altext3); white-space:nowrap; }

.argus-login .al-field{ margin-bottom:16px; }
.argus-login .al-field label{ display:block; font-size:12.5px; font-weight:600; color:var(--altext2); margin-bottom:8px; }
.argus-login .al-inp{ display:flex; align-items:center; gap:10px; height:48px; padding:0 14px; border-radius:12px; background:var(--panel); border:1px solid var(--hair); transition:border-color .18s, box-shadow .18s; }
.argus-login .al-inp:focus-within{ border-color:rgba(79,141,245,0.6); box-shadow:0 0 0 3px rgba(79,141,245,0.14); }
.argus-login .al-inp > svg{ width:17px; height:17px; color:var(--altext3); flex:none; }
.argus-login .al-inp input{ flex:1; min-width:0; background:none; border:none; outline:none; color:var(--altext); font-family:var(--alui); font-size:14.5px; height:100%; }
.argus-login .al-inp input::placeholder{ color:var(--altext3); }
.argus-login .al-inp input:-webkit-autofill,
.argus-login .al-inp input:-webkit-autofill:hover,
.argus-login .al-inp input:-webkit-autofill:focus{
  -webkit-text-fill-color:var(--altext);
  -webkit-box-shadow:0 0 0 1000px var(--panel) inset;
  box-shadow:0 0 0 1000px var(--panel) inset;
  caret-color:var(--altext);
  transition:background-color 9999s ease-in-out 0s;
}
.argus-login .al-peek{ background:none; border:none; cursor:pointer; color:var(--altext3); padding:4px; display:flex; }
.argus-login .al-peek:hover{ color:var(--altext2); }
.argus-login .al-peek svg{ width:17px; height:17px; }

.argus-login .al-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin:4px 0 22px; }
.argus-login .al-check{ display:flex; align-items:center; gap:9px; cursor:pointer; user-select:none; font-size:13px; color:var(--altext2); position:relative; }
.argus-login .al-check input{ position:absolute; opacity:0; width:18px; height:18px; margin:0; cursor:pointer; }
.argus-login .al-box{ width:18px; height:18px; border-radius:5px; border:1px solid var(--hair2); background:var(--panel); display:grid; place-items:center; transition:.15s; }
.argus-login .al-check input:checked + .al-box{ background:linear-gradient(120deg,var(--a),var(--b)); border-color:transparent; }
.argus-login .al-box svg{ width:11px; height:11px; color:#fff; opacity:0; transition:.15s; }
.argus-login .al-check input:checked + .al-box svg{ opacity:1; }
.argus-login .al-link{ font-size:13px; color:var(--a); text-decoration:none; font-weight:600; background:none; border:none; cursor:pointer; padding:0; font-family:var(--alui); }
.argus-login .al-link:hover{ text-decoration:underline; }

.argus-login .al-btn-primary{
  position:relative; overflow:hidden;
  display:flex; align-items:center; justify-content:center; gap:9px;
  width:100%; height:50px; border:none; border-radius:12px; cursor:pointer;
  background:linear-gradient(120deg,var(--a),var(--b)); color:#fff;
  font-family:var(--alui); font-weight:700; font-size:15px; letter-spacing:.01em;
  box-shadow:0 12px 30px -10px rgba(120,124,255,0.7);
  transition:transform .06s, box-shadow .2s;
}
.argus-login .al-btn-primary:hover{ box-shadow:0 16px 40px -8px rgba(120,124,255,0.85); }
.argus-login .al-btn-primary:active{ transform:translateY(1px); }
.argus-login .al-btn-primary:disabled{ opacity:.65; cursor:not-allowed; }
.argus-login .al-btn-primary .al-shine{ position:absolute; top:0; left:-140%; width:60%; height:100%; background:linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent); transform:skewX(-18deg); animation:al-shine 4.5s ease-in-out infinite; }
@keyframes al-shine{ 0%,100%{ left:-140%; } 55%,70%{ left:160%; } }
.argus-login .al-btn-primary > svg{ width:17px; height:17px; }
.argus-login .al-spin{ width:18px; height:18px; border:2px solid rgba(255,255,255,0.4); border-top-color:#fff; border-radius:50%; animation:al-spin .7s linear infinite; }
@keyframes al-spin{ to{ transform:rotate(360deg); } }

.argus-login .al-foot{ text-align:center; color:var(--altext2); font-size:13.5px; margin:24px 0 0; }
.argus-login .al-legal{ text-align:center; color:var(--altext3); font-size:11.5px; line-height:1.6; margin-top:30px; }

.argus-login .al-banner{ display:flex; align-items:flex-start; gap:10px; font-size:13px; border-radius:12px; padding:11px 14px; margin-bottom:18px; line-height:1.45; }
.argus-login .al-banner.err{ color:#ffb4b4; background:rgba(239,68,68,0.10); border:1px solid rgba(239,68,68,0.25); }
.argus-login .al-banner.info{ color:#bcd3ff; background:rgba(79,141,245,0.10); border:1px solid rgba(79,141,245,0.25); }
.argus-login .al-banner svg{ width:16px; height:16px; flex:none; margin-top:1px; }

@media (max-width:940px){
  .argus-login .al-stage{ grid-template-columns:1fr; }
  .argus-login .al-brandside{ display:none; }
  .argus-login .al-mobilebrand{ display:flex; }
  .argus-login .al-formside{ padding:40px 24px; }
}
@media (prefers-reduced-motion: reduce){
  .argus-login .al-blob,.argus-login .al-btn-primary .al-shine{ animation:none; }
}
`;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    const msg = sessionStorage.getItem('login_error');
    if (msg) sessionStorage.removeItem('login_error');
    return msg ?? '';
  });
  const [info, setInfo] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // This page is always English (see comment above), but i18n.ts sets
  // <html dir="rtl"> as soon as the app boots if the persisted language is
  // Hebrew — before this page ever renders. Left alone, the English copy
  // here inherits that RTL base direction: the grid columns swap sides and
  // bidi reshuffles neutral punctuation to the wrong edge (e.g. a trailing
  // "?" jumps in front of "Forgot password"). Force LTR while this page is
  // mounted and restore whatever the app had on the way out.
  useEffect(() => {
    const html = document.documentElement;
    const prevDir = html.dir;
    const prevLang = html.lang;
    html.dir = 'ltr';
    html.lang = 'en';
    return () => {
      html.dir = prevDir;
      html.lang = prevLang;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    let mustChangePassword = false;
    try {
      ({ mustChangePassword } = await login(email, password, keepSignedIn));
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
      return;
    }

    if (mustChangePassword) {
      navigate({ to: '/change-password' });
      setLoading(false);
      return;
    }

    // Whoever just signed in inherits NOTHING from whoever used this browser before them.
    // `login()` calls clearSession(), which now resets the tenant and acted-as club stores
    // too — so this holds on every branch below, including the catch, where the old code
    // left the previous session's context in place. That mattered most for a super admin:
    // `appGate` accepts any surviving club context as a valid selection, so a stale one sent
    // the next super admin straight into the club the last one was acting as.
    try {
      const { data: profile } = await api.post('/auth/profile');
      queryClient.clear();
      if (profile?.role === 'super_admin') {
        // Super admins have no club of their own — they pick an active club on a
        // dedicated screen; the context is already clear, so the choice is fresh.
        navigate({ to: '/choose-club' });
      } else if (!profile?.clubId || profile.role === 'pending') {
        navigate({ to: '/onboarding' });
      } else if (profile.role === 'player_view') {
        navigate({ to: '/player' });
      } else {
        navigate({ to: '/' });
      }
    } catch {
      queryClient.clear();
      navigate({ to: '/' });
    }
    setLoading(false);
  };

  const handleForgotPassword = () => {
    setError('');
    setInfo('Contact your club administrator to reset your password.');
  };

  const wordmark = (
    <span className="al-wm">
      <b>Argus</b>
      <i>Sports</i>
    </span>
  );

  return (
    <div className="argus-login">
      <style>{LOGIN_CSS}</style>
      <div className="al-stage">
        {/* ── Left — ambient brand panel ── */}
        <section className="al-brandside">
          <div className="al-grid" />
          <div className="al-blob" />

          <div className="al-topbar">
            <ArgusMark size={40} />
            {wordmark}
          </div>

          <div className="al-center">
            <ArgusMark size={132} className="al-eye" />
            <h2 className="al-headline">
              Every player.
              <br />
              Every metric.
              <br />
              <b>Always watching.</b>
            </h2>
            <p className="al-sub">
              The performance-intelligence platform that turns live match data into decisions — before the
              whistle blows.
            </p>
          </div>
        </section>

        {/* ── Right — sign-in form ── */}
        <section className="al-formside">
          <form className="al-card" onSubmit={handleLogin}>
            <div className="al-mobilebrand">
              <ArgusMark size={36} />
              {wordmark}
            </div>

            <div className="al-kicker">Welcome back</div>
            <h1 className="al-h1">Sign in to Argus Sports</h1>
            <p className="al-lede">Access your clubs, live matches and intelligence feeds.</p>

            {error && (
              <div className="al-banner err">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}
            {info && (
              <div className="al-banner info">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 100 2 1 1 0 000-2zm1 4a1 1 0 10-2 0v4a1 1 0 102 0v-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{info}</span>
              </div>
            )}

            <div className="al-field">
              <label htmlFor="al-email">Work email</label>
              <div className="al-inp">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
                <input
                  id="al-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@club.com"
                />
              </div>
            </div>

            <div className="al-field">
              <label htmlFor="al-pw">Password</label>
              <div className="al-inp">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <rect x="4" y="10" width="16" height="10" rx="2" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                </svg>
                <input
                  id="al-pw"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••••"
                />
                <button
                  type="button"
                  className="al-peek"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <path d="M6.61 6.61A18.5 18.5 0 0 0 2 12s3.5 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                      <path d="m2 2 20 20" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="al-row">
              <label className="al-check">
                <input
                  type="checkbox"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                />
                <span className="al-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                Keep me signed in
              </label>
              <button type="button" className="al-link" onClick={handleForgotPassword}>
                Forgot password?
              </button>
            </div>

            <button type="submit" className="al-btn-primary" disabled={loading}>
              <span className="al-shine" />
              {loading ? (
                <>
                  <span className="al-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                </>
              )}
            </button>

            <p className="al-foot">
              New to Argus Sports?{' '}
              <button type="button" className="al-link" onClick={() => setRequestOpen(true)}>
                Request access
              </button>
            </p>

            <p className="al-legal">By signing in you agree to our Terms and Privacy Policy.</p>
          </form>
        </section>
      </div>
      {requestOpen && <AccessRequestModal onClose={() => setRequestOpen(false)} />}
    </div>
  );
}
